/* eslint-disable import/no-cycle */
import { DataRecord } from '@cloudio-saas/datasource-types';
import { useStateXAtomSetter, useStateXValue } from '@cloudio-saas/statex';
import type { ItemID } from 'componentregistry';
import type { Platform } from 'componentregistry/platform/Platform';
import { SINGLETON } from 'componentregistry/registry/constants';
import { aliasAtom } from 'components/DataStore/state';
import { DataStoreTriggers } from 'components/DataStore/useDataStoreTriggers';
import { PlatformContext } from 'platform/PlatformProvider';
import { useContext, useEffect } from 'react';
import DSAttributesCache from 'store/DSAttributesCache';
import { globalCache } from 'utils/cache';
import { isEmpty } from 'utils/isEmpty';
import Store, { DataStoreType, MAX_ROWS } from './Store';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyStore = Store<any>;

const StoreCache = globalCache.get<Map<string, Map<string, AnyStore>>>(
  '_store_',
  () => {
    return new Map();
  },
);

function getKey(ds: string, alias: string) {
  return `${ds}-${alias}`;
}

function getPageMap(appUid: string, pageId: string): Map<string, AnyStore> {
  // TODO: Need to include appUid as well as part of the cache key as same pageId could exists in two or more applications
  let map = StoreCache.value.get(`${pageId}-${appUid}`);
  if (!map) {
    map = new Map<string, AnyStore>();
    StoreCache.value.set(`${pageId}-${appUid}`, map);
  }
  return map;
}

export function clearPageCache(appUid: string, pageId: string) {
  const map = StoreCache.value.get(`${pageId}-${appUid}`);
  if (map) {
    StoreCache.value.delete(`${pageId}-${appUid}`);
  }
  const dsMap = DSAttributesCache.promiseCache.get(appUid);
  if (dsMap) {
    Store.destroy();
  }
}

export function initStore<T = DataRecord>(props: {
  appUid: string;
  dsAppUid?: string;
  pageId: string;
  ds: string;
  alias: string;
  displayName: string;
  platform: Platform;
  programmatic: boolean;
  filterLocally: boolean;
  triggers?: DataStoreTriggers;
  itemId?: ItemID;
  itemType: DataStoreType;
  maxRows?: number;
  singleton: boolean;
  controlledBy?: string;
  cacheAll?: boolean;
}): Store<T> {
  const {
    ds,
    alias,
    displayName,
    platform,
    programmatic,
    filterLocally,
    dsAppUid,
    appUid,
    pageId,
    triggers,
    itemId,
    itemType,
    maxRows,
    singleton,
    controlledBy,
    cacheAll = false,
  } = props;
  // console.log('initStore.render.1');
  const key = getKey(ds, alias);
  let store = getPageMap(appUid, singleton ? SINGLETON : pageId).get(
    key,
  ) as Store<T>;

  if (!store) {
    store = new Store<T>({
      ds,
      alias,
      displayName,
      platform,
      programmatic,
      filterLocally,
      appUid,
      dsAppUid,
      pageId,
      triggers,
      itemId,
      itemType,
      maxRows,
      singleton,
      controlledBy,
    });
    store.cacheAll = cacheAll;
    platform.registerAlias({ appUid, pageId, alias, dataSource: ds });
    getPageMap(appUid, singleton ? SINGLETON : pageId).set(key, store);
  } else if (store.platform !== platform) {
    // happens when navigating to /help and back
    store.platform = platform;
  }
  if (store.itemId !== itemId) {
    store.itemId = itemId;
  }
  // the below values are not set during the initial creation of the store
  // in Page.tsx so we need to set them here
  if (triggers) {
    const {
      afterQueryTrigger,
      afterSaveTrigger,
      beforeCreateTrigger,
      beforeQueryTrigger,
      beforeSaveTrigger,
      onRecordActiveTrigger,
    } = triggers;
    store.afterQueryTrigger = afterQueryTrigger;
    store.beforeQueryTrigger = beforeQueryTrigger;
    store.beforeCreateTrigger = beforeCreateTrigger;
    store.beforeSaveTrigger = beforeSaveTrigger;
    store.afterSaveTrigger = afterSaveTrigger;
    store.onRecordActiveTrigger = onRecordActiveTrigger;
  }
  store.filterLocally = filterLocally;
  store.maxRows = maxRows || MAX_ROWS;
  // console.log('initStore.render.2');
  return store;
}

export default function useDataStore<T = DataRecord>({
  appUid,
  pageId,
  ds,
  alias,
  filterLocally = false,
  singleton = false,
  displayName,
  cacheAll = false,
  maxRows,
}: {
  appUid: string;
  pageId: string;
  ds: string;
  alias: string;
  filterLocally?: boolean;
  singleton?: boolean;
  displayName?: string;
  cacheAll?: boolean;
  maxRows?: number;
}): Store<T> {
  // console.log('useDataStore.render.1');
  const platform = useContext(PlatformContext);
  const programmatic = true;

  const setAliases = useStateXAtomSetter(aliasAtom, {
    params: { appUid, pageId },
  });

  useEffect(() => {
    if (alias && ds) {
      setAliases((a) => {
        if (a[alias]) {
          if (a[alias] !== ds) {
            platform.showError(
              `Alias ${alias} already exists for ${a[alias]} and cannot be re-used for ${ds}!`,
            );
          }
          return a;
        }
        return { ...a, [alias]: ds };
      });
    }
  }, [alias, appUid, ds, pageId, platform]);

  // console.log('useDataStore.render.2');
  return initStore<T>({
    alias,
    appUid,
    ds,
    displayName: displayName || alias,
    filterLocally,
    pageId,
    platform,
    programmatic,
    singleton,
    itemType: 'DataStore',
    cacheAll,
    maxRows,
  });
}

export function getStoreCreateIfNew<T = DataRecord>({
  appUid,
  pageId,
  ds,
  alias,
  filterLocally = false,
  singleton = false,
  displayName,
  platform,
}: {
  appUid: string;
  pageId: string;
  ds: string;
  alias: string;
  filterLocally?: boolean;
  singleton?: boolean;
  displayName?: string;
  platform: Platform;
}): Store<T> {
  // console.log('useDataStore.render.1');
  const programmatic = true;
  // console.log('useDataStore.render.2');
  return initStore<T>({
    alias,
    appUid,
    ds,
    displayName: displayName || alias,
    filterLocally,
    pageId,
    platform,
    programmatic,
    singleton,
    itemType: 'DataStore',
  });
}

export function getStore<T = DataRecord>({
  appUid,
  pageId,
  ds,
  alias,
}: {
  appUid: string;
  pageId: string;
  ds: string;
  alias: string;
}): Store<T> | null {
  if (isEmpty(ds) || isEmpty(alias)) {
    return null;
  }
  const key = getKey(ds, alias);
  return (
    (getPageMap(appUid, pageId).get(key) as Store<T>) ??
    (getPageMap(appUid, SINGLETON).get(key) as Store<T>) ??
    null
  );
}

export function useIsStoreInitialQueryFired(
  store: Store<any> | null | undefined,
): boolean {
  return useStateXValue(
    store?.initialQueryFiredPath ?? ['__initialQueryFiredPath__'],
    false,
  );
}
